import React, { useContext, useEffect, useState } from "react";
import { cn, mapEdgesToNodes, imageBuilder, getInviteDateLabels } from "../../lib/helpers";
import { graphql, useStaticQuery } from "gatsby";
import { SiteContext } from "../global/site-context";
import { SectionContext } from "./context";
import { SectionHeaders } from "./wrapper";
import { Card } from "../card/card";
import { CardContext } from "../card/context";
import { isPast, isFuture } from "date-fns";
import Calendar from "react-calendar";
import PortableText from "../portableText";
import { pageDocPath } from "../../../helpers";
import { ImageBuilder } from "../global/image-builder";
import { ArrowRight } from "../icon/arrow-left";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalendarDays, faClock, faLocationDot } from "@fortawesome/free-solid-svg-icons";
import { EVENT_TYPE_LABELS } from "../../templates/invite";

import "react-calendar/dist/Calendar.css";
import * as styles from "./calendar.module.css";

const query = graphql`
  query InviteListQuery {
    docs: allSanityInvite(
      sort: { fields: [startDate], order: DESC }
      filter: { status: { in: ["published", "review"] } }
    ) {
      edges {
        node {
          ...cardNodeInvite
        }
      }
    }
  }
`;

const ONBOARDING_CATEGORY = "9e37e7a3-1453-4a6d-a9ec-c693a20c0b04";
const KOALA_CATEGORY = "6ee87d4d-fa91-43a5-af4e-a0da73caa69e";

const dateMatch = (date, dateToMatch) =>
  new Date(date).getDate() === new Date(dateToMatch).getDate() &&
  new Date(date).getMonth() === new Date(dateToMatch).getMonth() &&
  new Date(date).getFullYear() === new Date(dateToMatch).getFullYear();

const dateBasedBg = (invite) =>
  isFuture(new Date(invite.startDate))
    ? invite.url || !invite.inviteOnly
      ? "bg-green-emerald"
      : "bg-green-emerald"
    : "bg-dark-20";

const CalendarView = ({ docList, filterCategory = null, exclude = null }) => {
  const [future, setFuture] = useState([]);
  const [past, setPast] = useState([]);
  const [value, _setValue] = useState([]);
  const [recurring, setRecurring] = useState([]);
  const now = new Date();

  // Filter by category if specified, and exclude categories if specified
  let filteredDocs = docList;

  if (filterCategory) {
    filteredDocs = filteredDocs.filter((doc) =>
      doc.categories && doc.categories.find(cat => cat._id === filterCategory)
    );
  }

  if (exclude) {
    filteredDocs = filteredDocs.filter((doc) =>
      !doc.categories || !doc.categories.find(cat => cat._id === exclude)
    );
  }

  const notRecurring = filteredDocs.filter((e) => !e.recurring);

  useEffect(() => {
    setPast(notRecurring.filter((i) => isPast(new Date(i.startDate))));
    setFuture(notRecurring.filter((i) => isFuture(new Date(i.startDate))).reverse());
    setRecurring(filteredDocs.filter((i) => i.recurring));
  }, [docList, filterCategory, exclude]);

  return (
    <>
      <Calendar
        value={value}
        // onChange={(_val, e) => {
        //   e.preventDefault();
        //   e.stopPropagation();
        // }}
        minDate={new Date(new Date(now).setMonth(now.getMonth() - 4))}
        maxDate={new Date(new Date(now).setMonth(now.getMonth() + 4))}
        minDetail="month"
        tileContent={({ date, view }) => {
          const match = filteredDocs.find((invite) =>
            dateMatch(date, invite.startDate)
          );
          return view === "month" ? (
            match ? (
              <>
                <div
                  className={cn(
                    // "h-4 w-4 rounded-full absolute top-1/2 left-1/2 mt-2 -ml-2",
                    // "md:top-px md:right-px md:bottom-px md:left-px md:opacity-50 md:rounded-lg md:m-0 md:h-auto md:w-auto",
                    "absolute top-px right-px bottom-px left-px opacity-40 rounded-lg",
                    dateBasedBg(match)
                  )}
                ></div>
                <p className="mt-0.5 relative hidden sm:block text-xs leading-tight">
                  {match.location.split(",")[0]}
                </p>
                <p className="sm:hidden text-xs mt-2">click</p>
                <a
                  href={`#${match._id}`}
                  className="absolute top-px right-px bottom-px left-px bg-black opacity-0 pointer-events-auto hover:opacity-5 blend-mode-overlay rounded-lg"
                ></a>
              </>
            ) : (
              dateMatch(date, now) && (
                <div className="absolute top-px right-px bottom-px left-px bg-light-40 rounded-lg"></div>
              )
            )
          ) : null;
        }}
        className={cn(
          "border-none w-full rounded-xl overflow-hidden mb-8 bg-dark-5",
          styles.reactCalendar
        )}
        tileClassName={cn(
          "relative h-16 flex flex-col items-center justify-start bg-white pointer-events-none",
          styles.reactCalendarTile
        )}
      />
      {recurring.length > 0 && (
        <div className="my-8 pt-4 border-t border-dark-30">
          <h2 className="text-lg font-semibold mb-4">Recurring Events</h2>
          <ul className="grid gap-4 grid-cols-1">
            {recurring.map((invite) => (
              <li key={invite.id} className="mb-4">
                <a id={invite._id} className="absolute -top-20"></a>
                <CardContext.Provider
                  value={{
                    ...invite,
                    showSummary: true,
                    skipLink: invite.inviteOnly,
                    cta: invite.inviteOnly
                      ? "Invite only"
                      : invite.url
                        ? "Register"
                        : "Coming soon",
                    url:
                      !invite.url && !invite.inviteOnly
                        ? false
                        : invite.url,
                    targetBlank: !!invite.url,
                    fullSummary: true,
                    hideType: invite.url && !invite.inviteOnly
                  }}
                >
                  <Card />
                </CardContext.Provider>
              </li>
            ))}
          </ul>
        </div>
      )}
      {future.length > 0 ? (
        <ul className="grid gap-4 grid-cols-1">
          {future.map((invite) => (
            <li key={invite.id} className="mb-4 relative">
              <a id={invite._id} className="absolute -top-20"></a>
              <CardContext.Provider
                value={{
                  ...invite,
                  showSummary: true,
                  skipLink: invite.inviteOnly || (!pageDocPath(invite) && !invite.url),
                  cta: invite.inviteOnly
                    ? "Invite only"
                    : (pageDocPath(invite) || invite.url)
                      ? "Register"
                      : "Coming soon",
                  url: pageDocPath(invite) || invite.url || false,
                  targetBlank: !pageDocPath(invite) && !!invite.url,
                  fullSummary: true,
                  hideType: invite.url && !invite.inviteOnly
                }}
              >
                <Card />
              </CardContext.Provider>
            </li>
          ))}
        </ul>
      ) : (
        <p className="md:ml-3 font-semibold text-auxiliary text-center">
          Loading...
        </p>
      )}
      {past.length > 0 && (
        <>
          <div className="my-8 pt-4 border-t border-dark-30">Recent Events</div>
          <ul className="grid gap-4 grid-cols-1 opacity-60 hover:opacity-100">
            {past.slice(0, 2).map((invite) => (
              <li key={invite.id} className="mb-4">
                <CardContext.Provider
                  value={{
                    ...invite,
                    skipLink: !invite.url,
                    cta: invite.url ? "View details" : "Invite only",
                    showSummary: true,
                    fullSummary: true,
                    targetBlank: !!invite.url,
                  }}
                >
                  <Card />
                </CardContext.Provider>
              </li>
            ))}
          </ul>
        </>
      )}
    </>
  );
};

const useInviteDocs = () => {
  const { docs } = useStaticQuery(query);
  const { generateUnpublishedPages } = React.useContext(SiteContext);
  const all = docs && mapEdgesToNodes(docs);

  return generateUnpublishedPages ? all : all?.filter((d) => d.status === "published");
};

export const InviteList = ({ section }) => {
  const docList = useInviteDocs();
  return <CalendarView docList={docList} exclude={KOALA_CATEGORY} />;
};

export const InviteListPriorityExpress = ({ section }) => {
  const docList = useInviteDocs();
  return <CalendarView docList={docList} filterCategory={ONBOARDING_CATEGORY} />;
};

export const InviteListKoala = ({ section }) => {
  const docList = useInviteDocs();
  return <CalendarView docList={docList} filterCategory={KOALA_CATEGORY} />;
};

const FeaturedEventBlock = ({ docList, filterCategory = null, exclude = null }) => {
  let filtered = docList;

  if (filterCategory) {
    filtered = filtered.filter((doc) =>
      doc.categories?.find((cat) => cat._id === filterCategory)
    );
  }
  if (exclude) {
    filtered = filtered.filter((doc) =>
      !doc.categories?.find((cat) => cat._id === exclude)
    );
  }

  const upcoming = filtered
    .filter((i) => !i.recurring && i.eventType !== "on-demand" && isFuture(new Date(i.startDate)))
    .reverse();

  const featured = upcoming.find((i) => i?.isFeatured) || upcoming[0];

  if (!featured) return null;

  const image = featured.graphic || featured.mainImage;
  const imageSrc = image?.asset
    ? imageBuilder(image).width(1200).height(630).quality(80).auto("format").url()
    : null;
  const imageFit = featured.graphic ? "object-cover" : "object-contain";
  const featuredPath = pageDocPath(featured);
  const featuredExternal = !featuredPath && !!featured.url;
  const { dateLabel, timeLabel } = getInviteDateLabels(featured);

  return (
    <a
      href={featuredPath || featured.url || undefined}
      target={featuredExternal ? "_blank" : undefined}
      rel={featuredExternal ? "noopener noreferrer" : undefined}
      className="grid grid-cols-1 md:grid-cols-2 rounded-2xl shadow-xl overflow-hidden bg-light-90 group hover:shadow-2xl transition-shadow duration-200"
    >
      <div className="relative min-h-64 md:min-h-96 bg-dark-10 overflow-hidden group-hover:opacity-90 transition-opacity duration-300">
        <span className="absolute top-4 left-4 z-10 px-3 py-1 rounded-full bg-light-80 text-sm text-black shadow-sm">
          Next up
        </span>
        {imageSrc ? (
          <img src={imageSrc} alt={featured.title} className={cn("absolute inset-0 w-full h-full transform transition-size duration-300 group-hover:scale-105", imageFit)} />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-dark-30">
            <ImageBuilder src="/static/img/cr-logo-icon.svg" className="w-24 opacity-20" />
          </div>
        )}
      </div>
      <div className="p-8 md:p-10 flex flex-col justify-center">
        <h2 className="text-5xl md:text-6xl font-semibold tracking-tight mb-4 leading-snug">
          {featured.title}
        </h2>
        {featured._rawExcerpt && (
          <div className="text-dark-60 mb-6 leading-relaxed">
            <PortableText blocks={featured._rawExcerpt} />
          </div>
        )}
        <ul className="flex flex-col gap-2 font-medium text-secondary mb-6">
          {featured.recurring ? (
            <li>Recurring event</li>
          ) : (
            <>
              {dateLabel && (
                <li className="flex items-center gap-2">
                  <FontAwesomeIcon icon={faCalendarDays} className="w-4 text-green-emerald opacity-80" />
                  {dateLabel}
                </li>
              )}
              {timeLabel && (
                <li className="flex items-center gap-2">
                  <FontAwesomeIcon icon={faClock} className="w-4 text-green-emerald opacity-80" />
                  {timeLabel}
                </li>
              )}
            </>
          )}
          {featured.location && (
            <li className="flex items-center gap-2">
              <FontAwesomeIcon icon={faLocationDot} className="w-4 text-green-emerald opacity-80" />
              {featured.location || EVENT_TYPE_LABELS[featured.eventType]}
            </li>
          )}
        </ul>
        <div className="inline-flex items-center gap-1.5 text-sm font-medium px-4 py-2 rounded-full bg-black text-white self-start">
          {featured.inviteOnly ? "View event" : "Register"}
          <ArrowRight />
        </div>
      </div>
    </a>
  );
};

export const InviteListFeatured = ({ section }) => {
  const docList = useInviteDocs();
  return <FeaturedEventBlock docList={docList} exclude={KOALA_CATEGORY} />;
};

const InviteGridBlock = ({ docList, filterCategory = null, exclude = null, section }) => {
  const sectionContext = useContext(SectionContext);
  let filtered = docList;

  if (filterCategory) {
    filtered = filtered.filter((doc) =>
      doc.categories?.find((cat) => cat._id === filterCategory)
    );
  }
  if (exclude) {
    filtered = filtered.filter((doc) =>
      !doc.categories?.find((cat) => cat._id === exclude)
    );
  }

  const allUpcoming = filtered
    .filter((i) => !i.recurring && i.eventType !== "on-demand" && isFuture(new Date(i.startDate)))
    .reverse();

  const featured = allUpcoming.find((i) => i?.isFeatured) || allUpcoming[0];

  const upcoming = featured
    ? allUpcoming.filter((i) => i._id !== featured._id)
    : allUpcoming;

  if (!upcoming.length) return null;

  return (
    <>
      <SectionHeaders {...section} isDarkBg={sectionContext.isDarkBg} />
      <ul className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
        {upcoming.map((invite) => (
          <li key={invite._id} className="relative">
            <a id={invite._id} className="absolute -top-20" />
            <CardContext.Provider
              value={{
                ...invite,
                showSummary: true,
                fullSummary: true,
                showAllImages: true,
                hideType: true,
                hideByline: true,
                skipLink: !pageDocPath(invite) && !invite.url,
                cta: invite.inviteOnly ? "View event" : "Register",
                url: pageDocPath(invite) || invite.url || false,
                targetBlank: !pageDocPath(invite) && !!invite.url,
                makeThumbnail: true,
              }}
            >
              <Card />
            </CardContext.Provider>
          </li>
        ))}
      </ul>
    </>
  );
};

export const InviteListGrid = ({ section }) => {
  const docList = useInviteDocs();
  return <InviteGridBlock docList={docList} exclude={KOALA_CATEGORY} section={section} />;
};
